import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { CMSTag } from '@/client/types/cms';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tagKeys } from './tag.query';
import { cmsKeys } from '@/client/api/cms/cms.query';

const TAGS = '/api/admin/tags';
const TIMEOUT = { timeout: 30_000 };

const invalidateTagCatalog = (qc: ReturnType<typeof useQueryClient>, tagid?: number) => {
  qc.invalidateQueries({ queryKey: tagKeys.all });
  // Preserve legacy invalidation used by CMS modules.
  qc.invalidateQueries({ queryKey: [...cmsKeys.all, 'tags'] });
  if (tagid != null) {
    qc.invalidateQueries({ queryKey: tagKeys.detail(tagid) });
    qc.invalidateQueries({ queryKey: tagKeys.usage(tagid) });
    qc.invalidateQueries({ queryKey: tagKeys.entities(tagid) });
    qc.invalidateQueries({ queryKey: tagKeys.cms(tagid) });
  }
};

export const useCreateTagMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (body: Partial<CMSTag>) => {
    return request<any>(TAGS, 'POST', body, TIMEOUT);
  }, 'Failed to create tag');
  return useMutation({
    mutationFn: (body: Partial<CMSTag>) => fn(body),
    onSuccess: () => invalidateTagCatalog(qc),
  });
};

export const useUpdateTagMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { tagid: number } & Partial<CMSTag>) => {
    const { tagid, ...body } = args;
    return request<any>(`${TAGS}/${tagid}`, 'PATCH', body, TIMEOUT);
  }, 'Failed to update tag');
  return useMutation({
    mutationFn: (args: { tagid: number } & Partial<CMSTag>) => fn(args),
    onSuccess: (_data, vars) => invalidateTagCatalog(qc, vars.tagid),
  });
};

export const useDeleteTagMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { tagid: number; hard?: boolean }) => {
    return request<any>(`${TAGS}/${args.tagid}?hard=${!!args.hard}`, 'DELETE', {}, TIMEOUT);
  }, 'Failed to delete tag');
  return useMutation({
    mutationFn: (args: { tagid: number; hard?: boolean }) => fn(args),
    onSuccess: (_data, vars) => invalidateTagCatalog(qc, vars.tagid),
  });
};

export interface TagMergeResult {
  success: boolean;
  source_tagid: number;
  source_name: string;
  target_tagid: number;
  target_name: string;
  source_deactivated: boolean;
  synonyms_added: string[];
  assignments: {
    entities: { moved: number; deduplicated: number; total: number };
    cms_pages: { moved: number; deduplicated: number; total: number };
    users: { moved: number; deduplicated: number; total: number };
  };
  children_reparented: number;
}

export const useMergeTagMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { sourceTagid: number; targetTagid: number }) => {
      const raw = await request<any>(
        `${TAGS}/${args.sourceTagid}/merge`,
        'POST',
        { target_tagid: args.targetTagid, add_as_synonym: true },
        TIMEOUT,
      );
      return (raw?.data?.data ?? raw?.data ?? raw) as TagMergeResult;
    },
    'Failed to merge tags',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, vars) => {
      invalidateTagCatalog(qc, vars.sourceTagid);
      invalidateTagCatalog(qc, vars.targetTagid);
      qc.invalidateQueries({ queryKey: ['entities'] });
      qc.invalidateQueries({ queryKey: cmsKeys.all });
    },
  });
};

export const useActivateTagMutation = () => {
  const update = useUpdateTagMutation();
  return useMutation({
    mutationFn: (args: { tagid: number; active: boolean }) =>
      update.mutateAsync({ tagid: args.tagid, active: args.active }),
  });
};

export const useBulkAssignTagEntitiesMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { tagid: number; add?: number[]; remove?: number[] }) => {
      return request<any>(
        `${TAGS}/${args.tagid}/entities`,
        'PUT',
        { add: args.add ?? [], remove: args.remove ?? [] },
        TIMEOUT,
      );
    },
    'Failed to bulk-assign tag',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, vars) => {
      invalidateTagCatalog(qc, vars.tagid);
      qc.invalidateQueries({ queryKey: ['entities'] });
    },
  });
};
